import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDate, formatTimeAgo } from "@/lib/utils";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { CreateJobDialog } from "@/components/dashboard/CreateJobDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Search,
  ArrowRight,
  Clock,
  Filter,
  RefreshCw,
} from "lucide-react";

const STATUS_FILTERS = [
  "all",
  "researching",
  "in_progress",
  "waiting_email",
  "success",
  "failed",
  "cancelled",
];

export function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const loadJobs = async () => {
    try {
      const params = { limit: 100 };
      if (filter !== "all") params.status = filter;
      const data = await api.listJobs(params);
      setJobs(data.jobs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Load jobs error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
  }, [filter]);

  const filteredJobs = jobs.filter((j) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      j.ip?.toLowerCase().includes(term) ||
      j.blacklist?.toLowerCase().includes(term) ||
      j.blacklist_name?.toLowerCase().includes(term)
    );
  });

  return (
    <div data-testid="jobs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            Delisting Jobs
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {total} total jobs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadJobs}
            data-testid="btn-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="btn-new-job">
            <Plus className="w-4 h-4 mr-2" /> New Job
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search IP, domain, blacklist..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="input-search-jobs"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter className="w-4 h-4 text-muted-foreground mr-1" />
          {STATUS_FILTERS.map((s) => (
            <Button
              key={s}
              variant={filter === s ? "default" : "ghost"}
              size="sm"
              onClick={() => setFilter(s)}
              className="text-xs capitalize"
              data-testid={`filter-${s}`}
            >
              {s === "all" ? "All" : s.replace("_", " ")}
            </Button>
          ))}
        </div>
      </div>

      {/* Jobs List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {filter !== "all"
                ? `No ${filter.replace("_", " ")} jobs found.`
                : "No jobs yet."}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredJobs.map((job) => (
                <Link
                  key={job.job_id}
                  to={`/jobs/${job.job_id}`}
                  className="flex items-center justify-between p-4 hover:bg-secondary/30 transition-colors"
                  data-testid={`job-row-${job.job_id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <StatusBadge status={job.status} pulse />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">
                          {job.ip}
                        </span>
                        <span className="text-muted-foreground">&rarr;</span>
                        <span className="text-sm truncate">
                          {job.blacklist_name || job.blacklist}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                        {job.job_id.slice(0, 8)}...
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatTimeAgo(job.created_at)}
                      </div>
                      {job.error && (
                        <div className="text-xs text-red-400 mt-0.5 max-w-[200px] truncate">
                          {job.error}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateJobDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => loadJobs()}
      />
    </div>
  );
}
